import { Reveal } from "./Reveal";
import { EVENT, IMAGES } from "./data";
import { Music, UtensilsCrossed, Shirt, Users, Check, ArrowUpRight, CalendarDays, MapPin, UserCheck, Ticket } from "lucide-react";

const ICONS = { Music, UtensilsCrossed, Shirt, Users };
const DETAIL_ICONS = [CalendarDays, MapPin, UserCheck, Ticket];

export default function EventFestival() {
  return (
    <section id="event" data-testid="event-section" className="relative border-t border-white/5">
      {/* Banner */}
      <div className="relative min-h-[70vh] flex items-end overflow-hidden">
        <img src={IMAGES.festival} alt={EVENT.name} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/30" />
        <div className="absolute inset-0 bg-grain opacity-[0.04]" />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 w-full pb-16 pt-28">
          <Reveal>
            <p className="overline mb-5">Proposed Event · {EVENT.location}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-heading font-light text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter max-w-4xl">
              {EVENT.name} <span className="text-gold">{EVENT.year}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-sm sm:text-base uppercase tracking-[0.22em] text-foreground/70">
              {EVENT.pillars.join("  ·  ")}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-4 font-heading italic text-2xl sm:text-3xl text-gold/90">{EVENT.lead}</p>
          </Reveal>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 lg:py-28">
        <Reveal>
          <p className="max-w-2xl text-lg font-light leading-relaxed text-foreground/70">{EVENT.blurb}</p>
        </Reveal>

        {/* Details */}
        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {EVENT.details.map((d, i) => {
            const Icon = DETAIL_ICONS[i];
            return (
              <Reveal key={d.label} delay={i * 0.08} className="bg-background">
                <div data-testid={`event-detail-${i}`} className="h-full p-7 lg:p-8">
                  <Icon size={20} className="text-gold" />
                  <p className="mt-4 text-xs uppercase tracking-[0.2em] text-foreground/40">{d.label}</p>
                  <p className="mt-1 font-light text-foreground/90">{d.value}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Highlights */}
        <div className="mt-24">
          <Reveal>
            <p className="overline mb-8">Event Highlights</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {EVENT.highlights.map((h, i) => {
              const Icon = ICONS[h.icon];
              return (
                <Reveal key={h.title} delay={i * 0.1}>
                  <div data-testid={`event-highlight-${i}`} className="h-full border border-white/10 p-8 hover:border-gold/40 transition-colors duration-500">
                    <Icon size={26} className="text-gold" />
                    <h3 className="mt-5 font-heading text-2xl font-medium tracking-tight">{h.title}</h3>
                    <p className="mt-3 font-light leading-relaxed text-foreground/60">{h.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        {/* Why sponsor */}
        <div className="mt-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="overline mb-6">Why Sponsor?</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h3 className="font-heading font-light text-3xl sm:text-4xl leading-tight tracking-tight">
                Invest in people. Invest in community.
              </h3>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-5">
              {EVENT.whySponsor.map((w, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <li data-testid={`event-why-${i}`} className="flex items-start gap-3 text-foreground/75 font-light">
                    <Check size={18} className="text-gold shrink-0 mt-1" />
                    {w}
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>

        {/* Sponsorship packages */}
        <div className="mt-24">
          <Reveal>
            <p className="overline mb-8">Sponsorship Packages</p>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {EVENT.packages.map((p, i) => (
              <Reveal key={p.tier} delay={(i % 3) * 0.1}>
                <div
                  data-testid={`event-package-${i}`}
                  className={`h-full flex flex-col p-8 border transition-colors duration-500 ${
                    p.featured ? "border-gold/60 bg-gold/[0.06]" : "border-white/10 hover:border-white/25"
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-4">
                    <h3 className="font-heading text-2xl font-medium tracking-tight">{p.tier}</h3>
                    {p.featured && (
                      <span className="text-[10px] uppercase tracking-[0.2em] text-gold border border-gold/50 px-2 py-1">Top Tier</span>
                    )}
                  </div>
                  <p className="mt-3 font-heading text-4xl text-gold">{p.price}</p>
                  {p.note && <p className="text-xs text-foreground/40 mt-1">{p.note}</p>}
                  <ul className="mt-6 space-y-3">
                    {p.perks.map((perk) => (
                      <li key={perk} className="flex items-start gap-3 text-sm font-light text-foreground/70">
                        <Check size={16} className="text-gold shrink-0 mt-0.5" />
                        {perk}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* CTA */}
        <Reveal delay={0.1}>
          <div className="mt-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border border-white/10 p-8 lg:p-12">
            <div>
              <h3 className="font-heading text-3xl lg:text-4xl font-light tracking-tight">Be part of something bigger.</h3>
              <p className="mt-2 font-light text-foreground/60">Partner with us to celebrate culture and empower communities.</p>
            </div>
            <a
              href="mailto:9jaclicks1@gmail.com?subject=Afro%20Urban%20Festival%202027%20Sponsorship"
              data-testid="event-sponsor-button"
              className="shrink-0 inline-flex items-center gap-2 bg-gold text-primary-foreground px-9 py-4 text-xs uppercase tracking-[0.2em] hover:bg-gold/90 transition-all duration-500"
            >
              Become a Sponsor <ArrowUpRight size={16} />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
